"use client";

import { useEffect } from "react";
import { useAtomValue } from "jotai";
import { candleColorsAtom, linkBuySellToChartAtom } from "./chart-prefs";
import { hexToHslTriplet } from "./color";
import { useTheme } from "./theme";

/**
 * Pushes the user's candle colours onto the --buy/--sell tokens so the entire UI (
 * order book sides, buy/sell buttons, PnL) moves with the chart.
 *
 * Without this, picking the colourblind-safe preset gives you a blue/orange chart next to a
 * red/green order book: the two encodings disagree, which is worse than either alone, and
 * defeats the point of choosing the preset.
 *
 * Writes inline custom properties on <html>, which override the stylesheet's per-theme
 * values. Removing them restores whatever the active theme defines: hence the theme
 * dependency: on a theme flip we must re-assert (or re-clear) on top of the new defaults.
 */
export const useBuySellTokens = () => {
  const colors = useAtomValue(candleColorsAtom);
  const linked = useAtomValue(linkBuySellToChartAtom);
  const { theme } = useTheme();

  useEffect(() => {
    const root = document.documentElement;

    if (!linked) {
      root.style.removeProperty("--buy");
      root.style.removeProperty("--sell");
      return;
    }

    const buy = hexToHslTriplet(colors.up);
    const sell = hexToHslTriplet(colors.down);
    if (buy) root.style.setProperty("--buy", buy);
    if (sell) root.style.setProperty("--sell", sell);

    return () => {
      root.style.removeProperty("--buy");
      root.style.removeProperty("--sell");
    };
  }, [colors, linked, theme]);
};
